import Phaser from "phaser";
import { CraftingWindow } from "../windows/crafting-window";
import { CraftingButton } from "../../../ui/crafting-button";

type CraftButtonStyle = "weapon" | "ammo" | "support";

type StationButtonConfig = {
  stationName: string;
  label: string;
  style: CraftButtonStyle;
  icon: string;
};

export class CraftScreen {
  private readonly scene: Phaser.Scene;
  private readonly windowManager: any;
  private readonly panel: Phaser.GameObjects.Container;
  private buttons: CraftingButton[] = [];
  private craftingWindow: CraftingWindow | null = null;
  private allItems: any[] = [];
  private allRecipes: any[] = [];
  private stationLevel: number = 1;
  private canUpgrade: boolean = false;
  private upgradeMaterials: { itemId: number; quantity: number }[] = [];
  private craftingWindowVisible: boolean = false;
  private pendingStationName: string | null = null;

  constructor(
    scene: Phaser.Scene,
    windowManager: any,
    panel: Phaser.GameObjects.Container,
  ) {
    this.scene = scene;
    this.windowManager = windowManager;
    this.panel = panel;
    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    if ((window as any).socket) {
      (window as any).socket.on("crafting-recipes", (data: any) => {
        this.allItems = data.items || [];
        this.allRecipes = data.recipes || [];
        this.stationLevel = data.stationLevel || 1;
        this.canUpgrade = data.canUpgrade || false;
        this.upgradeMaterials = data.upgradeMaterials || [];

        if (
          this.pendingStationName &&
          data?.stationName === this.pendingStationName
        ) {
          this.pendingStationName = null;
          this.showCraftingWindow(data.stationName, this.allRecipes);
        }
      });
    }
  }

  show(): void {
    // Hide all windows from the window manager
    this.windowManager.hideAll();
    
    // Create buttons
    this.createButtons();
    this.showButtons();
  }

  private createButtons(): void {
    const buttonConfigs = this.getStationButtonConfigs();
    if (buttonConfigs.length === 0) {
      return;
    }

    const BTN_W = 100;
    const SPACING = 40;
    const totalWidth = buttonConfigs.length * BTN_W + (buttonConfigs.length - 1) * SPACING;
    const startX = (this.scene.scale.width - totalWidth) / 2 + BTN_W / 2 + 16;
    const startY = this.scene.scale.height / 2 - 40;

    buttonConfigs.forEach(({ stationName, label, style, icon }, index) => {
      const x = startX + index * (BTN_W + SPACING);
      const y = startY;

      const button = new CraftingButton({
        scene: this.scene,
        x,
        y,
        text: label,
        icon,
        style: style,
        onClick: () => this.onStationButtonClicked(stationName),
      });
      button.setDepth(10000);
      this.panel.add(button);
      this.buttons.push(button);
    });
  }

  private getStationButtonConfigs(): StationButtonConfig[] {
    const playerStations =
      ((window as any).player?.data?.stations as { name: string }[] | undefined) || [];
    const stationNames = Array.from(
      new Set(
        playerStations
          .map((station) => this.normalizeStationName(station.name))
          .filter((stationName) => Boolean(stationName) && stationName !== "ARMOR"),
      ),
    );

    return stationNames.map((stationName) => {
      const stationLower = stationName.toLowerCase();
      const iconKey = `crafting-${stationLower}-icon`;
      const fallbackIcon = "crafting-weapon-icon";
      const icon = this.scene.textures.exists(iconKey) ? iconKey : fallbackIcon;

      return {
        stationName,
        label: this.toTitleLabel(stationName),
        style: this.getStyleForStation(stationName),
        icon,
      };
    });
  }

  private normalizeStationName(rawStationName: string): string {
    return rawStationName.replace(/-station$/i, "").toUpperCase();
  }

  private toTitleLabel(stationName: string): string {
    return stationName
      .toLowerCase()
      .split("_")
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(" ");
  }

  private getStyleForStation(stationName: string): CraftButtonStyle {
    if (stationName === "AMMO") {
      return "ammo";
    }
    if (stationName === "SUPPORT") {
      return "support";
    }
    return "weapon";
  }

  private onStationButtonClicked(stationName: string): void {
    if (!(window as any).socket) {
      return;
    }

    this.pendingStationName = stationName;
    this.hideButtons();
    (window as any).socket.emit("crafting-recipes", {
      stationName,
    });
  }

  private showCraftingWindow(stationName: string, recipes: any[]): void {
    // Hide buttons when opening crafting window
    this.hideButtons();

    // Create crafting window if it doesn't exist
    if (!this.craftingWindow) {
      this.craftingWindow = new CraftingWindow(this.scene);
      this.craftingWindow.setScreenContainer(this.panel);
      this.craftingWindow.setLayoutMode("fullscreen");
      // Store reference to original hide method
      const originalHide = this.craftingWindow.hide.bind(this.craftingWindow);
      // Override hide to show buttons again when closing
      this.craftingWindow.hide = () => {
        originalHide();
        if (this.craftingWindowVisible) {
          this.showButtons();
          this.craftingWindowVisible = false;
        }
      };
    }

    // Show the crafting window with filtered recipes and station level
    this.craftingWindow.show(
      this.allItems,
      recipes,
      stationName,
      this.stationLevel,
      this.upgradeMaterials,
      this.canUpgrade,
    );
    this.craftingWindowVisible = true;
  }

  private hideButtons(): void {
    this.buttons.forEach((button) => {
      button.setVisible(false);
      button.disableInteractive();
    });
  }

  private showButtons(): void {
    this.buttons.forEach((button) => {
      button.setVisible(true);
      button.setInteractive();
    });
  }

  hide(): void {
    this.pendingStationName = null;
    this.buttons.forEach((button) => button.destroy());
    this.buttons = [];
    if (this.craftingWindow) {
      this.craftingWindow.hide();
      this.craftingWindow = null;
    }
  }
}
